import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import DataLookupField from '../../../components/DataLookupField';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package
} from 'lucide-react';

import { useAuth } from '../../../contexts/AuthContext';

export function DataManagementPanel() {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [orderStats, setOrderStats] = useState({ total_orders: 0, date_first_order: '', date_last_order: '' });
  const [expCounts, setExpCounts] = useState({});
  const [archiveStart, setArchiveStart] = useState('');
  const [archiveEnd, setArchiveEnd] = useState('');
  const [deleteStart, setDeleteStart] = useState('');
  const [deleteEnd, setDeleteEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const { labels: expLabels } = useExpenditureLabels();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, countsRes] = await Promise.all([
        api.get('/api/admin/data-management/order-stats'),
        api.get('/api/admin/data-management/expenditure-counts')
      ]);
      setOrderStats(statsRes.data);
      setExpCounts(countsRes.data.counts || {});
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  if (loading) return <LoadingSpinner />;

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }); } catch { return d; }
  };

  const headerCodes = [
    { key: 'account', label: expLabels.exp_code_1 },
    { key: 'restaurant', label: expLabels.exp_code_2 },
    { key: 'department_code', label: expLabels.exp_code_3 },
  ];
  const lineCodes = [
    { key: 'gl', label: expLabels.exp_code_4 },
    { key: 'property_code', label: expLabels.exp_code_5 },
    { key: 'nominal_code', label: expLabels.exp_code_6 },
  ];

  return (
    <div className="max-w-4xl" data-testid="data-management-panel">
      <h1 className="text-2xl font-bold mb-1">Data Management</h1>
      <p className="text-lg font-medium text-muted-foreground mb-6">Data Operations</p>

      {/* Section 1: Archive Order File */}
      <div className="border rounded-lg p-6 mb-6" data-testid="dm-archive-order-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">1</span>
          Archive Order File
        </h3>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <h4 className="text-red-600 font-bold text-lg mb-1">WARNING</h4>
          <p className="text-sm text-red-800">This function enables you to archive selected historical order data. The procedure is not reversible.</p>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          In addition, it may also be prudent for you to take advantage of the Export Function. From the Order listing page in the User Interface, you can export your data using a variety of supported file formats for use in other databases or applications.
        </p>
        <div className="space-y-2 mb-4">
          <div className="flex gap-8">
            <span className="w-40 text-sm text-muted-foreground">Total Orders on file:</span>
            <span className="text-sm font-medium" data-testid="dm-total-orders">{orderStats.total_orders}</span>
          </div>
          <div className="flex gap-8">
            <span className="w-40 text-sm text-muted-foreground">Date of First Order:</span>
            <span className="text-sm font-medium">{formatDate(orderStats.date_first_order)}</span>
          </div>
          <div className="flex gap-8">
            <span className="w-40 text-sm text-muted-foreground">Date of Last Order:</span>
            <span className="text-sm font-medium">{formatDate(orderStats.date_last_order)}</span>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-4 border-t pt-4">
          Based upon the Order Date, please stipulate the date range of the orders you wish to archive.
        </p>
        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-4">
            <Label className="w-24 text-sm">Start Date:</Label>
            <Input type="date" value={archiveStart} onChange={e => setArchiveStart(e.target.value)} className="w-48 text-sm" data-testid="dm-archive-start" />
            <Button variant="ghost" size="sm" onClick={() => setArchiveStart('')}><X className="h-3 w-3" /></Button>
          </div>
          <div className="flex items-center gap-4">
            <Label className="w-24 text-sm">End Date:</Label>
            <Input type="date" value={archiveEnd} onChange={e => setArchiveEnd(e.target.value)} className="w-48 text-sm" data-testid="dm-archive-end" />
            <Button variant="ghost" size="sm" onClick={() => setArchiveEnd('')}><X className="h-3 w-3" /></Button>
          </div>
        </div>
        <div className="border-t pt-3">
          <p className="text-xs text-muted-foreground mb-1">As per the Data Retention Policy, we do not delete any order data. However, once it is archived, we retain the order header, order line, attachments and event log.</p>
          <p className="text-xs text-muted-foreground">This can be seen by users that have been given the permission under User Register{'>'} Permissions. In the user interface, the archived orders can be seen on the Order Listing Page under Order Menu{'>'} Orders - Archived.</p>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button size="sm" onClick={() => toast.info('Archive functionality will be wired up')} data-testid="dm-archive-next-btn">Next</Button>
          <Button variant="outline" size="sm" onClick={() => { setArchiveStart(''); setArchiveEnd(''); }} data-testid="dm-archive-reset-btn">Reset</Button>
        </div>
      </div>

      {/* Section 2: Delete Bulk Request */}
      <div className="border rounded-lg p-6 mb-6" data-testid="dm-delete-bulk-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">2</span>
          Delete Bulk Request
        </h3>
        <p className="text-sm text-muted-foreground mb-1">This function enables you to delete Requests with a DORMANT status.</p>
        <p className="text-sm text-muted-foreground mb-4">The procedure is not reversible so before continuing we strongly advise you to backup your data if needed.</p>
        <p className="text-sm text-muted-foreground mb-4 border-t pt-4">Based upon the Request Date, please specify the date range of the requests you wish to delete.</p>
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <Label className="w-24 text-sm">Start Date:</Label>
            <Input type="date" value={deleteStart} onChange={e => setDeleteStart(e.target.value)} className="w-48 text-sm" data-testid="dm-delete-start" />
            <Button variant="ghost" size="sm" onClick={() => setDeleteStart('')}><X className="h-3 w-3" /></Button>
          </div>
          <div className="flex items-center gap-4">
            <Label className="w-24 text-sm">End Date:</Label>
            <Input type="date" value={deleteEnd} onChange={e => setDeleteEnd(e.target.value)} className="w-48 text-sm" data-testid="dm-delete-end" />
            <Button variant="ghost" size="sm" onClick={() => setDeleteEnd('')}><X className="h-3 w-3" /></Button>
          </div>
        </div>
      </div>

      {/* Section 3: Expenditure Code Import */}
      <div className="border rounded-lg p-6 mb-6" data-testid="dm-expenditure-import-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">3</span>
          Expenditure Code Import
        </h3>
        <div className="grid grid-cols-3 gap-6">
          {/* Header Codes */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Expenditure Code for Header</h4>
            <div className="space-y-3">
              {headerCodes.map(c => (
                <div key={c.key} className="border rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-primary font-bold text-sm underline" data-testid={`dm-count-${c.key}`}>{expCounts[c.key] || 0}</span>
                    <span className="text-[10px] text-muted-foreground">records</span>
                    <span className="text-sm ml-1">{c.label}</span>
                  </div>
                  <Button size="sm" variant="default" className="text-xs h-7" data-testid={`dm-upload-${c.key}`}>
                    <Download className="h-3 w-3 mr-1" /> Upload
                  </Button>
                </div>
              ))}
            </div>
          </div>
          {/* Line Codes */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Expenditure Code for Line</h4>
            <div className="space-y-3">
              {lineCodes.map(c => (
                <div key={c.key} className="border rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-primary font-bold text-sm underline" data-testid={`dm-count-${c.key}`}>{expCounts[c.key] || 0}</span>
                    <span className="text-[10px] text-muted-foreground">records</span>
                    <span className="text-sm ml-1">{c.label}</span>
                  </div>
                  <Button size="sm" variant="default" className="text-xs h-7" data-testid={`dm-upload-${c.key}`}>
                    <Download className="h-3 w-3 mr-1" /> Upload
                  </Button>
                </div>
              ))}
            </div>
          </div>
          {/* Cost Code */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Cost Code</h4>
            <div className="border rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-primary font-bold text-sm underline" data-testid="dm-count-cost_centre">{expCounts.cost_centre || 0}</span>
                <span className="text-[10px] text-muted-foreground">records</span>
                <span className="text-sm ml-1">Cost Centre</span>
              </div>
              <Button size="sm" variant="default" className="text-xs h-7" data-testid="dm-upload-cost_centre">
                <Download className="h-3 w-3 mr-1" /> Upload
              </Button>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-4 border-t pt-3">
          Please note that the displayed records include the total count of both active and inactive expenditure codes.
        </p>
      </div>
    </div>
  );
}
